<template>
  <v-container fluid grid-list-md>
    <CorePageIntro>
      <template slot="content">
        Electricity generated by co-generation and solar onsite is generally
        more efficient than the electricity produced by the marginal power plant
        on the grid. We're measuring the carbon intensity of grid power
        available from your local utility at every hour, and comparing that to
        the carbon intensity of your behind-the-meter power production. Using
        equivalency calculators provided by the EPA, the carbon emissions you
        avoided are converted into emission equivalents.
      </template>
    </CorePageIntro>

    <v-layout row wrap>
      <v-flex xs12 sm12 md9 class="pr-2">
        <v-card outlined>
          <highcharts
            ref="highcharts"
            :options="options"
            v-if="options"
          ></highcharts>
        </v-card>
      </v-flex>
      <!-- annual summary -->
      <v-flex xs12 sm12 md3>

        <CoreCardStat class="mb-5" title="2018 Total Produced">
          <template slot="subtitle"> </template>
          <template slot="stat_value">
            <span class="primary--text">7,442 MT</span>
          </template>
          <template slot="stat_description">
            MT = Metric Ton
          </template>
        </CoreCardStat>                

        <CoreCardStat class="mb-5" title="2018 Total Avoided">
          <template slot="subtitle"> </template>
          <template slot="stat_value">
            <span class="light-green--text text--darken--5">2,303 MT</span>
          </template>
          <template slot="stat_description">
            2,303 = 31% of Total
          </template>
        </CoreCardStat>                

        <center>
          <v-btn
            :href="images.report"
            medium
            dark
            color="primary"
            target="_report"
            class="mt-5 text-center"
          >
            <span style="color:white;">Download Report</span>
          </v-btn>
        </center>
      </v-flex>
    </v-layout>

    <!-- cards -->
    <v-card outlined class="pa-4 mt-2 mb-1" color="blue-grey lighten-5">
      <h5 class="text-left mt-1 mb-2">
        2,303 MT is the equivalent of Co2 emissions** from ...
      </h5>
      <v-layout row wrap>
        <v-flex xs4>
          <v-card outlined>
            <v-card-title>
              <v-layout row wrap>
                <v-flex xs5>
                  <v-img :src="images.coal" alt="Coal Car" height="100"></v-img>
                </v-flex>
                <v-flex xs7>
                  <div>
                    <div class="display-1 mt-2 font-weight-bold compress">
                      252,415
                    </div>
                    <div class="title">Pounds Coal Burned</div>
                  </div>
                </v-flex>
              </v-layout>
            </v-card-title>
          </v-card>
        </v-flex>
        <v-flex xs4>
          <v-card outlined>
            <v-card-title>
              <v-layout row wrap>
                <v-flex xs5>
                  <v-img :src="images.auto" alt="Car" height="100"></v-img>
                </v-flex>
                <v-flex xs7>
                  <div>
                    <div class="display-1 mt-2 font-weight-bold compress">
                      25,960
                    </div>
                    <div class="title">Gallons Gas Burned</div>
                  </div>
                </v-flex>
              </v-layout>
            </v-card-title>
          </v-card>
        </v-flex>

        <v-flex xs4>
          <v-card outlined>
            <v-card-title>
              <v-layout row wrap>
                <v-flex xs5>
                  <v-img
                    :src="images.oil"
                    alt="Oil Barrel"
                    height="100"
                  ></v-img>
                </v-flex>
                <v-flex xs7>
                  <div>
                    <div class="display-1 mt-2 font-weight-bold compress">
                      534
                    </div>
                    <div class="title">Barrels Consumed</div>
                  </div>
                </v-flex>
              </v-layout>
            </v-card-title>
          </v-card>
        </v-flex>
        <v-flex xs12 text-right>
          <em>
            **Calculations based on the EPA's
            <a
              href="https://www.epa.gov/energy/greenhouse-gases-equivalencies-calculator-calculations-and-references"
              target="_epa"
              >Greenhouse Gases Equivalencies Calculator</a
            >.
          </em>
        </v-flex>
      </v-layout>
    </v-card>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import COLORS from '@/util/colors'
export default {
  name: 'Sustain',
  data() {
    return {
      options: false,
      images: {
        oil: require('@/assets/img/reports/oil.png'),
        auto: require('@/assets/img/reports/auto.png'),
        coal: require('@/assets/img/reports/coal.png'),
        report: require('@/assets/img/reports/sustainability.png')
      }
    }
  },
  computed: {
    ...mapGetters(['getFacilityName'])
  },
  mounted() {
    const produced = [1456, 1987, 2835, 1264]
    const avoided = [217, 745, 1117, 224]
    const axis = ['Q1', 'Q2', 'Q3', 'Q4']

    this.options = {
      chart: {
        type: 'column'
      },
      title: {
        text: 'Produced vs. Avoided'
      },
      exporting: {
        filename: this.getFacilityName + '_Sustinability',
        fallbackToExportServer: false
      },
      credits: {
        enabled: false
      },
      xAxis: {
        categories: axis,
        crosshair: true
      },
      yAxis: {
        title: {
          text: 'Output (MT)',
          style: {
            color: COLORS.hex.power
          }
        },
        stackLabels: {
          enabled: true,
          style: {
            fontWeight: 'bold',
            color: '#555'
          }
        },
        labels: {
          format: '{value:,.0f} MT',
          style: {
            color: COLORS.hex.power
          }
        }
      },
      tooltip: {
        shared: true,
        crosshairs: true,
        backgroundColor: COLORS.hex.crosshairs,
        borderColor: COLORS.hex.black
      },
      legend: {
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom'
      },
      plotOptions: {
        column: {
          stacking: 'normal'
        }
      },
      series: [
        {
          name: 'Avoided (MT)',
          data: avoided,
          color: '#D6E9C6'
        },
        {
          name: 'Produced (MT)',
          data: produced,
          color: '#335b98'
        }
      ]
    }
  }
}
</script>
<style lang="scss" scoped>
.card-deck-row {
  background-color: rgba(51, 91, 152, 0.1);
  padding: 10px 6px 10px 6px;
}
.card-img-top {
  padding-top: 12px;
}
.underline {
  border-bottom: 1px solid #cccccc;
}
.card-text {
  font-weight: 500;
  font-size: 22px;
  letter-spacing: -1px;
}
</style>
